/**
 * ComplianceTab (KOPS-511) — Unified Compliance Command Center.
 *
 * Split-panel: left is a unified incident feed (AI flags + doc reviews +
 * transaction risk), right is a polymorphic detail panel that renders
 * different content based on the selected incident type.
 *
 * Backend: GET /api/compliance/dashboard (compliance-dashboard.controller.js)
 * Scoping: internal sees all, broker sees brokerage, agent sees self.
 * P0 for May 1 demo.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShieldAlt,
  faFileContract,
  faTriangleExclamation,
  faCheckCircle,
  faRobot,
  faClock,
  faUserShield,
  faXmark,
  faArrowRight,
} from '@fortawesome/free-solid-svg-icons';

import axiosInstance from '../../../helpers/api';
import {
  SplitPanel,
  SearchBar,
  ListItem,
  FilterPills,
  ProfileHeader,
  InlineForm,
  NavyTextarea,
  NavyInput,
  GoldButton,
  OutlineButton,
  colors,
  fonts,
} from '../shared';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type IncidentType = 'ai_flag' | 'doc_review' | 'transaction';
type IncidentStatus = 'open' | 'resolved' | 'dismissed';
type Severity = 'high' | 'medium' | 'low';

interface Incident {
  id: string;
  type: IncidentType;
  title: string;
  subtitle?: string;
  entity_id?: string;
  agent_id?: string;
  transaction_id?: string;
  severity: Severity;
  status: IncidentStatus;
  created_at: string;
  // ai_flag
  regulatory_basis?: string;
  violation_details?: any;
  context_data?: any;
  // doc_review
  compliance_score?: number;
  compliance_flags?: any;
  ai_analysis?: any;
  document_url?: string;
  // transaction
  compliance_status?: string;
  file_number?: string;
  stage?: string;
  property_data?: any;
}

interface DashboardSummary {
  ai_safety_score: number;
  ai_safety_flags_open: number;
  doc_review_queue: number;
  transaction_risk: number;
  total_open: number;
  scope: string;
  days: number;
}

interface ComplianceTabProps {
  brokerageId?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const TYPE_OPTIONS = [
  { key: 'all', label: 'All' },
  { key: 'ai_flag', label: 'AI Flags' },
  { key: 'doc_review', label: 'Documents' },
  { key: 'transaction', label: 'Transactions' },
];

const STATUS_OPTIONS = [
  { key: 'open', label: 'Open' },
  { key: 'resolved', label: 'Resolved' },
  { key: 'dismissed', label: 'Dismissed' },
  { key: 'all', label: 'All' },
];

const ComplianceTab: React.FC<ComplianceTabProps> = () => {
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('open');
  const [selected, setSelected] = useState<Incident | null>(null);
  const [showResolveForm, setShowResolveForm] = useState(false);
  const [showDismissForm, setShowDismissForm] = useState(false);
  const [resolveNotes, setResolveNotes] = useState('');
  const [dismissReason, setDismissReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionAlert, setActionAlert] = useState<string | null>(null);

  // ---- Fetch ---------------------------------------------------------------
  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get('/compliance/dashboard', {
        params: { type: typeFilter, status: statusFilter, days: 30, limit: 100 },
      });
      if (res.data?.success) {
        setIncidents(res.data.incidents || []);
        setSummary(res.data.summary || null);
      } else {
        setError(res.data?.error || 'Failed to load compliance data');
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load compliance data');
    } finally {
      setLoading(false);
    }
  }, [typeFilter, statusFilter]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  // ---- Derived: filter + search -------------------------------------------
  const filteredIncidents = useMemo(() => {
    if (!search.trim()) return incidents;
    const q = search.toLowerCase();
    return incidents.filter(
      (i) =>
        i.title?.toLowerCase().includes(q) ||
        i.subtitle?.toLowerCase().includes(q) ||
        i.file_number?.toLowerCase().includes(q)
    );
  }, [incidents, search]);

  // ---- Actions -------------------------------------------------------------
  const handleResolve = async () => {
    if (!selected) return;
    setActionLoading(true);
    try {
      await axiosInstance.post(`/compliance/incident/${selected.type}/${selected.id}/resolve`, {
        notes: resolveNotes || null,
      });
      setActionAlert('✅ Incident resolved');
      setShowResolveForm(false);
      setResolveNotes('');
      await fetchDashboard();
      setSelected(null);
    } catch (err: any) {
      setActionAlert(`❌ ${err?.response?.data?.error || err?.message || 'Resolve failed'}`);
    } finally {
      setActionLoading(false);
      setTimeout(() => setActionAlert(null), 4000);
    }
  };

  const handleDismiss = async () => {
    if (!selected) return;
    setActionLoading(true);
    try {
      await axiosInstance.post(`/compliance/incident/${selected.type}/${selected.id}/dismiss`, {
        reason: dismissReason || null,
      });
      setActionAlert('✅ Incident dismissed');
      setShowDismissForm(false);
      setDismissReason('');
      await fetchDashboard();
      setSelected(null);
    } catch (err: any) {
      setActionAlert(`❌ ${err?.response?.data?.error || err?.message || 'Dismiss failed'}`);
    } finally {
      setActionLoading(false);
      setTimeout(() => setActionAlert(null), 4000);
    }
  };

  // ---- Render --------------------------------------------------------------
  return (
    <>
      {/* Summary cards strip (below the hub tab bar) */}
      <SummaryStrip summary={summary} loading={loading} />

      {actionAlert && (
        <div
          style={{
            background: actionAlert.startsWith('✅') ? '#d4edda' : '#f8d7da',
            color: actionAlert.startsWith('✅') ? colors.green : colors.red,
            padding: '8px 24px',
            borderBottom: `1px solid ${colors.border}`,
            fontSize: '13px',
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          {actionAlert}
        </div>
      )}

      <SplitPanel
        left={
          <LeftPanel
            loading={loading}
            error={error}
            incidents={filteredIncidents}
            selected={selected}
            onSelect={(i) => {
              setSelected(i);
              setShowResolveForm(false);
              setShowDismissForm(false);
            }}
            search={search}
            onSearchChange={setSearch}
            typeFilter={typeFilter}
            onTypeChange={setTypeFilter}
            statusFilter={statusFilter}
            onStatusChange={setStatusFilter}
          />
        }
        right={
          selected ? (
            <RightPanel
              incident={selected}
              showResolveForm={showResolveForm}
              showDismissForm={showDismissForm}
              resolveNotes={resolveNotes}
              dismissReason={dismissReason}
              actionLoading={actionLoading}
              onOpenResolve={() => {
                setShowResolveForm(true);
                setShowDismissForm(false);
              }}
              onOpenDismiss={() => {
                setShowDismissForm(true);
                setShowResolveForm(false);
              }}
              onCancelForms={() => {
                setShowResolveForm(false);
                setShowDismissForm(false);
              }}
              onResolveNotesChange={setResolveNotes}
              onDismissReasonChange={setDismissReason}
              onResolve={handleResolve}
              onDismiss={handleDismiss}
            />
          ) : (
            <EmptyDetailState />
          )
        }
      />
    </>
  );
};

// ===========================================================================
// Summary strip (4 metric cards below the hub tab bar)
// ===========================================================================

const SummaryStrip: React.FC<{ summary: DashboardSummary | null; loading: boolean }> = ({
  summary,
  loading,
}) => {
  const cards = [
    {
      icon: faShieldAlt,
      label: 'AI Safety Score',
      value: summary ? `${summary.ai_safety_score}%` : '—',
      color: colors.green,
      sub: summary ? `${summary.ai_safety_flags_open} flags open` : '',
    },
    {
      icon: faRobot,
      label: 'Document Review Queue',
      value: summary ? String(summary.doc_review_queue) : '—',
      color: colors.blue,
      sub: 'Pending broker review',
    },
    {
      icon: faTriangleExclamation,
      label: 'Transaction Risk',
      value: summary ? String(summary.transaction_risk) : '—',
      color: colors.orange,
      sub: 'High-severity open',
    },
    {
      icon: faFileContract,
      label: 'Total Open',
      value: summary ? String(summary.total_open) : '—',
      color: colors.gold2,
      sub: summary ? `Scope: ${summary.scope} · last ${summary.days}d` : '',
    },
  ];

  return (
    <div
      style={{
        background: colors.offBg,
        borderBottom: `1px solid ${colors.border}`,
        padding: '16px 24px',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: '12px',
        flexShrink: 0,
      }}
    >
      {cards.map((c, i) => (
        <div
          key={i}
          style={{
            background: colors.white,
            border: `1px solid ${colors.border}`,
            borderLeft: `4px solid ${c.color}`,
            borderRadius: '8px',
            padding: '14px 18px',
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
          }}
        >
          <div
            style={{
              width: '38px',
              height: '38px',
              borderRadius: '50%',
              background: `${c.color}22`,
              border: `2px solid ${c.color}55`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <FontAwesomeIcon icon={c.icon} style={{ color: c.color, fontSize: '15px' }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: fonts.display, fontSize: '24px', color: colors.navy, lineHeight: 1 }}>
              {loading ? '…' : c.value}
            </div>
            <div style={{ fontSize: '11px', fontWeight: 600, color: colors.navy, marginTop: '2px' }}>
              {c.label}
            </div>
            {c.sub && <div style={{ fontSize: '10px', color: colors.muted, marginTop: '1px' }}>{c.sub}</div>}
          </div>
        </div>
      ))}
    </div>
  );
};

// ===========================================================================
// Left panel — search + filters + incident list
// ===========================================================================

interface LeftPanelProps {
  loading: boolean;
  error: string | null;
  incidents: Incident[];
  selected: Incident | null;
  onSelect: (i: Incident) => void;
  search: string;
  onSearchChange: (v: string) => void;
  typeFilter: string;
  onTypeChange: (v: string) => void;
  statusFilter: string;
  onStatusChange: (v: string) => void;
}

const LeftPanel: React.FC<LeftPanelProps> = ({
  loading,
  error,
  incidents,
  selected,
  onSelect,
  search,
  onSearchChange,
  typeFilter,
  onTypeChange,
  statusFilter,
  onStatusChange,
}) => (
  <>
    <div style={{ padding: '12px', borderBottom: `1px solid ${colors.navy3}` }}>
      <div style={{ marginBottom: '8px' }}>
        <SearchBar value={search} onChange={onSearchChange} placeholder="Search incidents..." />
      </div>
      <div style={{ marginBottom: '6px' }}>
        <FilterPills options={TYPE_OPTIONS} activeKey={typeFilter} onChange={onTypeChange} />
      </div>
      <FilterPills options={STATUS_OPTIONS} activeKey={statusFilter} onChange={onStatusChange} />
    </div>

    <div style={{ flex: 1, overflowY: 'auto' }}>
      {loading ? (
        <div style={{ padding: '32px', textAlign: 'center', color: colors.muted }}>
          Loading incidents…
        </div>
      ) : error ? (
        <div style={{ padding: '20px', color: colors.red, fontSize: '12px' }}>{error}</div>
      ) : incidents.length === 0 ? (
        <div style={{ padding: '40px 20px', textAlign: 'center', color: colors.muted }}>
          <FontAwesomeIcon icon={faCheckCircle} style={{ fontSize: '32px', marginBottom: '8px' }} />
          <div style={{ fontSize: '13px' }}>No incidents match the current filters</div>
        </div>
      ) : (
        incidents.map((i) => (
          <ListItem
            key={`${i.type}-${i.id}`}
            selected={selected?.id === i.id && selected?.type === i.type}
            onClick={() => onSelect(i)}
            title={
              <span>
                <IncidentTypeIcon type={i.type} />
                <span style={{ marginLeft: '8px' }}>{i.title}</span>
              </span>
            }
            subtitle={i.subtitle}
            meta={formatRelativeTime(i.created_at)}
            rightContent={<SeverityBadge severity={i.severity} />}
          />
        ))
      )}
    </div>
  </>
);

// ===========================================================================
// Right panel — polymorphic detail
// ===========================================================================

interface RightPanelProps {
  incident: Incident;
  showResolveForm: boolean;
  showDismissForm: boolean;
  resolveNotes: string;
  dismissReason: string;
  actionLoading: boolean;
  onOpenResolve: () => void;
  onOpenDismiss: () => void;
  onCancelForms: () => void;
  onResolveNotesChange: (v: string) => void;
  onDismissReasonChange: (v: string) => void;
  onResolve: () => void;
  onDismiss: () => void;
}

const RightPanel: React.FC<RightPanelProps> = ({
  incident,
  showResolveForm,
  showDismissForm,
  resolveNotes,
  dismissReason,
  actionLoading,
  onOpenResolve,
  onOpenDismiss,
  onCancelForms,
  onResolveNotesChange,
  onDismissReasonChange,
  onResolve,
  onDismiss,
}) => {
  const isOpen = incident.status === 'open';
  return (
    <div>
      <ProfileHeader
        title={incident.title}
        metaItems={[
          { icon: faClock, text: new Date(incident.created_at).toLocaleString() },
          ...(incident.subtitle ? [{ text: incident.subtitle }] : []),
          ...(incident.regulatory_basis ? [{ icon: faUserShield, text: incident.regulatory_basis }] : []),
        ]}
        badges={
          <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
            <SeverityBadge severity={incident.severity} />
            <StatusBadge status={incident.status} />
          </div>
        }
        rightContent={
          isOpen ? (
            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
              <GoldButton onClick={onOpenResolve}>
                <FontAwesomeIcon icon={faCheckCircle} /> Resolve
              </GoldButton>
              <OutlineButton onClick={onOpenDismiss}>
                <FontAwesomeIcon icon={faXmark} /> Dismiss
              </OutlineButton>
            </div>
          ) : null
        }
      />

      {showResolveForm && (
        <InlineForm
          visible
          title="Resolve Incident"
          variant="gold"
          onClose={onCancelForms}
        >
          <label style={{ fontSize: '11px', color: colors.muted, fontWeight: 600, display: 'block', marginBottom: '6px' }}>
            Resolution notes (optional)
          </label>
          <NavyTextarea
            rows={3}
            value={resolveNotes}
            onChange={(e) => onResolveNotesChange(e.target.value)}
            placeholder="What action was taken?"
            style={{ marginBottom: '10px' }}
          />
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <OutlineButton onClick={onCancelForms} disabled={actionLoading}>
              Cancel
            </OutlineButton>
            <GoldButton onClick={onResolve} disabled={actionLoading}>
              {actionLoading ? 'Resolving…' : 'Mark Resolved'}
            </GoldButton>
          </div>
        </InlineForm>
      )}

      {showDismissForm && (
        <InlineForm
          visible
          title="Dismiss Incident"
          onClose={onCancelForms}
        >
          <label style={{ fontSize: '11px', color: colors.muted, fontWeight: 600, display: 'block', marginBottom: '6px' }}>
            Dismissal reason
          </label>
          <NavyInput
            value={dismissReason}
            onChange={(e) => onDismissReasonChange(e.target.value)}
            placeholder="e.g. false positive, policy exception…"
            style={{ marginBottom: '10px' }}
          />
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <OutlineButton onClick={onCancelForms} disabled={actionLoading}>
              Cancel
            </OutlineButton>
            <GoldButton onClick={onDismiss} disabled={actionLoading}>
              {actionLoading ? 'Dismissing…' : 'Confirm Dismiss'}
            </GoldButton>
          </div>
        </InlineForm>
      )}

      {/* Polymorphic body */}
      <div style={{ padding: '0 24px 24px 24px' }}>
        {incident.type === 'ai_flag' && <AIFlagDetail incident={incident} />}
        {incident.type === 'doc_review' && <DocReviewDetail incident={incident} />}
        {incident.type === 'transaction' && <TransactionRiskDetail incident={incident} />}
      </div>
    </div>
  );
};

// ---- Polymorphic body components ------------------------------------------

const SectionCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div
    style={{
      background: colors.white,
      border: `1px solid ${colors.border}`,
      borderLeft: `4px solid ${colors.gold}`,
      borderRadius: '8px',
      padding: '16px 20px',
      marginBottom: '12px',
    }}
  >
    <div
      style={{
        fontSize: '11px',
        fontWeight: 700,
        color: colors.navy,
        textTransform: 'uppercase',
        letterSpacing: '1px',
        marginBottom: '10px',
      }}
    >
      {title}
    </div>
    {children}
  </div>
);

const KeyValue: React.FC<{ k: string; v: React.ReactNode }> = ({ k, v }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: `1px solid ${colors.border}`, fontSize: '13px' }}>
    <span style={{ color: colors.muted }}>{k}</span>
    <span style={{ color: colors.navy, fontWeight: 500, textAlign: 'right' }}>{v}</span>
  </div>
);

const AIFlagDetail: React.FC<{ incident: Incident }> = ({ incident }) => {
  const details = incident.violation_details || {};
  const context = incident.context_data || {};
  return (
    <>
      <SectionCard title="Violation Summary">
        <KeyValue k="Violation type" v={incident.title} />
        <KeyValue k="Chatbot" v={incident.subtitle || '—'} />
        <KeyValue k="Regulatory basis" v={incident.regulatory_basis || '—'} />
        <KeyValue k="Severity" v={<SeverityBadge severity={incident.severity} />} />
      </SectionCard>

      {Object.keys(details).length > 0 && (
        <SectionCard title="Violation Details">
          <pre
            style={{
              background: colors.card,
              padding: '10px',
              borderRadius: '6px',
              fontSize: '11px',
              color: colors.navy,
              overflow: 'auto',
              maxHeight: '220px',
              margin: 0,
            }}
          >
            {JSON.stringify(details, null, 2)}
          </pre>
        </SectionCard>
      )}

      {Object.keys(context).length > 0 && (
        <SectionCard title="Context">
          <pre
            style={{
              background: colors.card,
              padding: '10px',
              borderRadius: '6px',
              fontSize: '11px',
              color: colors.navy,
              overflow: 'auto',
              maxHeight: '220px',
              margin: 0,
            }}
          >
            {JSON.stringify(context, null, 2)}
          </pre>
        </SectionCard>
      )}
    </>
  );
};

const DocReviewDetail: React.FC<{ incident: Incident }> = ({ incident }) => {
  const flags = incident.compliance_flags || {};
  const analysis = incident.ai_analysis || {};
  return (
    <>
      <SectionCard title="Document Review">
        <KeyValue k="Document" v={incident.title} />
        <KeyValue k="Compliance score" v={incident.compliance_score != null ? `${incident.compliance_score}/100` : '—'} />
        <KeyValue k="Transaction" v={incident.transaction_id || '—'} />
        <KeyValue k="Agent" v={incident.agent_id || '—'} />
      </SectionCard>

      {Object.keys(flags).length > 0 && (
        <SectionCard title="Compliance Flags">
          <pre
            style={{
              background: colors.card,
              padding: '10px',
              borderRadius: '6px',
              fontSize: '11px',
              color: colors.navy,
              overflow: 'auto',
              maxHeight: '200px',
              margin: 0,
            }}
          >
            {JSON.stringify(flags, null, 2)}
          </pre>
        </SectionCard>
      )}

      {Object.keys(analysis).length > 0 && (
        <SectionCard title="AI Analysis">
          <pre
            style={{
              background: colors.card,
              padding: '10px',
              borderRadius: '6px',
              fontSize: '11px',
              color: colors.navy,
              overflow: 'auto',
              maxHeight: '280px',
              margin: 0,
            }}
          >
            {JSON.stringify(analysis, null, 2)}
          </pre>
        </SectionCard>
      )}

      {incident.document_url && (
        <SectionCard title="Document">
          <a
            href={incident.document_url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: colors.gold, fontSize: '13px', fontWeight: 600, textDecoration: 'none' }}
          >
            Open document <FontAwesomeIcon icon={faArrowRight} />
          </a>
        </SectionCard>
      )}
    </>
  );
};

const TransactionRiskDetail: React.FC<{ incident: Incident }> = ({ incident }) => {
  const prop = incident.property_data || {};
  return (
    <>
      <SectionCard title="Transaction">
        <KeyValue k="File #" v={incident.file_number || '—'} />
        <KeyValue k="Stage" v={incident.stage || '—'} />
        <KeyValue k="Compliance status" v={incident.compliance_status || '—'} />
        <KeyValue k="Compliance score" v={incident.compliance_score != null ? `${incident.compliance_score}/100` : '—'} />
        <KeyValue k="Agent" v={incident.agent_id || '—'} />
      </SectionCard>

      {Object.keys(prop).length > 0 && (
        <SectionCard title="Property">
          <KeyValue k="Address" v={(prop as any).address || '—'} />
          <KeyValue k="Price" v={(prop as any).price ? `$${Number((prop as any).price).toLocaleString()}` : '—'} />
          <KeyValue k="City" v={(prop as any).city || '—'} />
        </SectionCard>
      )}
    </>
  );
};

// ===========================================================================
// Helpers — badges, icons, time formatting
// ===========================================================================

const IncidentTypeIcon: React.FC<{ type: IncidentType }> = ({ type }) => {
  const icon =
    type === 'ai_flag' ? faRobot : type === 'doc_review' ? faFileContract : faTriangleExclamation;
  const color =
    type === 'ai_flag' ? colors.gold2 : type === 'doc_review' ? colors.blue : colors.orange;
  return <FontAwesomeIcon icon={icon} style={{ color, fontSize: '11px' }} />;
};

const SeverityBadge: React.FC<{ severity: Severity }> = ({ severity }) => {
  const color =
    severity === 'high' ? colors.red : severity === 'medium' ? colors.orange : colors.green;
  return (
    <span
      style={{
        background: `${color}22`,
        color,
        fontSize: '10px',
        fontWeight: 700,
        padding: '2px 8px',
        borderRadius: '4px',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
      }}
    >
      {severity}
    </span>
  );
};

const StatusBadge: React.FC<{ status: IncidentStatus }> = ({ status }) => {
  const color =
    status === 'open' ? colors.blue : status === 'resolved' ? colors.green : colors.muted;
  return (
    <span
      style={{
        background: `${color}22`,
        color,
        fontSize: '10px',
        fontWeight: 700,
        padding: '2px 8px',
        borderRadius: '4px',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
      }}
    >
      {status}
    </span>
  );
};

const EmptyDetailState: React.FC = () => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      padding: '48px',
      color: colors.muted,
    }}
  >
    <FontAwesomeIcon icon={faShieldAlt} style={{ fontSize: '48px', marginBottom: '12px', color: colors.gold3 }} />
    <div style={{ fontSize: '15px', fontWeight: 600, color: colors.navy }}>Select an incident</div>
    <div style={{ fontSize: '12px', marginTop: '4px' }}>
      Choose an item from the list to review details and take action
    </div>
  </div>
);

function formatRelativeTime(dateStr: string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  return date.toLocaleDateString();
}

export default ComplianceTab;
